import React, {Fragment, useContext, useEffect, useState} from 'react';
import PropTypes from "prop-types";

import {useForm} from "react-hook-form";
import ModalContentWrapper from '../ModalContentWrapper';
import {getDataFromResponse} from "root/DataFunctions/BaseDataFns";
import SpinnerDiv from "root/Components/SpinnerDiv";
import {getLetterboxItemDetails, saveNewLetterboxItem, updateLetterboxItem} from "root/DataFunctions/LetterboxDataFns";
import DropdownLetterboxDecisions from "root/ReactHookForm/ControlWrappers/DropdownLetterboxDecisions";
import DropdownLetterboxItemTypes from "root/ReactHookForm/ControlWrappers/DropdownLetterboxItemTypes";
import DropdownLetterboxToUsers from "root/ReactHookForm/ControlWrappers/DropdownLetterboxToUsers";
import DropdownLetterboxToContacts from "root/ReactHookForm/ControlWrappers/DropdownLetterboxToContacts";
import {IconPath, LetterBoxDecision, LetterBoxType, MessageSource} from "root/DataFunctions/Constants";
import ErrorDiv from "root/ReactHookForm/ui/ErrorDiv";
import SingleFilePicker from "../../ReactHookForm/ControlWrappers/SingleFilePicker";
import {getChildImageUrl} from "root/DataFunctions/ChildDataFns";
import {ApplicationTypes, doesUserHaveApplication} from "root/DataFunctions/PageContentRoles";
import GlobalContext from "root/contexts/GlobalContext";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {getUserRelationAndPermission, getUserRoles} from "root/DataFunctions/UserDataFns";

export const ModalLetterboxModes = {
    CREATE: 'create',
    VIEW: 'view',
    EDIT: 'edit',
    DELETE: 'delete',
    ACTION: 'action',
    MARK_SENT: 'mark_sent',
    RESUBMIT: 're_submit',
}

const ModalAddLetterboxItem = (props) =>
{
    const [data, setData] = useState({ isLoading: props.letter_box_item_id !== null, data: [] });
    const [permissions, setPermissions] = useState({  isLoading: true, data: [] });
    const [newUsersCanMessage, setNewUsersCanMessage] = useState(false);
    const globals = useContext(GlobalContext);

    //////////////////////
    //Form and data functions
    //////////////////////
    const {register, control, handleSubmit, setFocus, watch, setValue, formState: {errors: formErrors}} = useForm(
        {
            defaultValues:
            {
                letterbox: {
                    to_uid:  { value : 0 },
                    from_uid: { value : 0 }, /* This field uses raw dropdown-select objects */
                    letterbox_comment: '',
                    reference_number: '',
                    letterbox_decision_type_id: { value: LetterBoxDecision.UNAPPROVED},
                    letterbox_item_type_id: { value : 0 },
                    document: null,
                    details: ''
                },
                message: {
                    title: '',
                    text: '',
                    comment: ''
                }
            }
        });

    const watchToId = watch("letterbox.to_uid");
    const watchFromId = watch("letterbox.from_uid");
    const watchDocument = watch("letterbox.document");

    const titles = {
        view: 'View Letterbox Item',
        edit: 'Edit Letterbox Item',
        create: 'New Letterbox Item',
        delete: 'Delete Letterbox Item',
        mark_sent: 'Mark Letterbox Item as SENT',
        re_submit: 'Re-submit for Approval',
        action: 'Action Letterbox Item',
    } ;

    const submitButtonText = {
        send: 'Approve & Send (as Message)',
        approve: 'Approve',
        return: 'Return to Sender',
        create: 'Submit for Approval',
        delete: 'Delete',
        mark_sent: 'Mark Sent',
        edit: 'Re-submit',
        re_submit: 'Re-submit',
        hold: 'Not Approved'
    } ;

    useEffect(() =>
    {
        if ( props.letter_box_item_id !== null )
        {
            getLetterboxItemDetails(props.child_uid, props.letter_box_item_id, (response) =>
            {
                let postData = getDataFromResponse(response)[0];

                if (postData.message !== undefined)
                {
                    // Set the form values to the new values loaded from the database
                    setValue('message', {
                        title: postData.message.title,
                        text: postData.message.text,
                        editor_comment: postData.message.approval_comment
                    });
                }

                setValue('letterbox',
                    {
                        letterbox_decision_type_id: {value: postData.letterbox_decision_type_id},
                        letterbox_item_type_id: {value: postData.letterbox_item_type_id},
                        from_uid: {value: postData.destination_user_id},
                        to_uid: {value: postData.to_user_id},
                        reference_number: postData.reference_number,
                        letterbox_comment: postData.comment,
                        post_id: postData.post_id,
                        details: postData.title,
                        document: postData.document_id
                    });

                setData({isLoading: false, data: postData});

                //Check if this user can message - so we offer forward as message button
                getUserRelationAndPermission(postData.enc_from_user_id, postData.enc_destination_user_id, (response) =>
                {
                    const userRolesData = getDataFromResponse(response)[0];

                    if (userRolesData !== undefined)
                    {
                        const permissions = userRolesData.permissions;
                        setPermissions({isLoading: false, data: permissions});
                    }
                });
            });
        }

    }, []);

    useEffect(() =>
    {
        if (watchToId.value > 0 && watchFromId.value > 0)
        {
            getUserRoles(watchToId.value, watchFromId.value, (response) =>
            {
                let roleData = getDataFromResponse(response)[0];
                const canMessage = roleData['is_messager'];
                setNewUsersCanMessage(canMessage);
            });
        }

    }, [watchToId, watchFromId]);

    //////////////////////
    //Formatting functions
    //////////////////////
    const Title = ( props ) =>
    {
        return <h3>{titles[props.mode]}</h3>
    }

    const isViewOnly = props.mode === ModalLetterboxModes.VIEW;

    const getSubmitButtonText = ( decision ) =>
    {
        //if we are actioning a letterbox item and it already has a message we must re-submit
        const newdecision = decision === 'approve' && data.data.message !== undefined && +data.data.source === MessageSource.LETTERBOX ? 're_submit' : decision;
        return submitButtonText[newdecision] ;
    }

    const getSubmitButtonStyle= ( decision ) =>
    {
        switch ( decision )
        {
            case 'delete':
            case 'return':
            case 'hold':
                return "btn btn-icon btn-danger btn-icon-right btn-small" ;
            default:
                return "btn btn-icon btn-primary btn-icon-right btn-small" ;
        }
    }

    const getSubmitButtonTitle= ( decision ) =>
    {
        switch ( decision )
        {
            case 'create':
                return 'Add to queue for approval by the letterbox approver.' ;
            case 'approve':
                if(data.data.post_id !== null){
                    return 'Re-submit to editor for approval.' ;
                }
                else{
                    return 'Mark as approved indicating the physical document can be sent to the recipient.' ;
                }
            case 'send':
                return 'Approve and send the document as a digital message to the recipient. If this is a child it will go to their editor first' ;
            case 'return':
                return 'Return the message back to the sender.' ;
            case 'hold':
                return 'Hold back. Inform the sender if needed.' ;
        }
    }

    const submitButton = (decision, onClick) =>
    {
        return (
            <button onClick={onClick} type='button' style={{marginRight: '10px', marginLeft: '10px'}} disabled={false}
                    className={getSubmitButtonStyle(decision)} title={getSubmitButtonTitle(decision)}>{getSubmitButtonText(decision)}
                <i className="et-save" />
            </button>
        )
    }

    //////////////////////
    //Action functions
    //////////////////////
    const onClickCancel = () =>
    {
        props.onSaveCompleted ('cancel');
        props.onClose() ;
    }

    const doUpdateLetterboxItem = ( formData, decision ) =>
    {
        let operation = '';

        switch (props.mode)
        {
            case ModalLetterboxModes.ACTION :
                switch (decision)
                {
                    case LetterBoxDecision.APPROVED_AND_FORWARDED:
                    case LetterBoxDecision.APPROVED_NOT_FORWARDED:
                        operation = 'approve';
                        break;
                    case LetterBoxDecision.NOT_APPROVED:
                        operation = 'reject';
                        break;
                    case LetterBoxDecision.RETURN_TO_SENDER:
                        operation = 'return';
                        break;
                }
                break;

            case ModalLetterboxModes.MARK_SENT :
                operation = 'mark_sent';
                break;

            case ModalLetterboxModes.EDIT :
                switch (decision)
                {
                    case LetterBoxDecision.APPROVED_AND_FORWARDED:
                        operation = 'approve';
                        break;

                    case LetterBoxDecision.NOT_APPROVED:
                        operation = 'reject';
                        break;

                    default:
                        operation = 're_submit';
                        break;
                }
                break;

        }

        const postData = {operation: operation, formData: formData};

        updateLetterboxItem( props.letter_box_item_id, postData, (response) =>
        {
            props.onSaveCompleted ('saved');
            props.onClose() ;
        })
    }

    const doCreateLetterboxItem = ( formData ) =>
    {
        const postData = {
            operation: 'create',
            formData: formData
        }

        saveNewLetterboxItem( postData, (response) =>
        {
            props.onSaveCompleted ('saved');
            props.onClose() ;
        })
    }

    const confirmShow = (title, message, action) =>
    {
        let cfrm = new VanillaModal ({
            title: title,
            content: message,
            buttons: VanillaModal.buttons.confirm
        })

        cfrm.show().once('dismiss', (modal, ev, button) => {
            if (button && button.value === true)
            {
                action() ;
            }
        });
    }

    const onSubmit = (data, decision) => {
        switch (props.mode)
        {
            case ModalLetterboxModes.CREATE:
                data.letterbox.decision = decision;
                doCreateLetterboxItem(data);
                break;

            case ModalLetterboxModes.EDIT:
                data.letterbox.letterbox_decision_type_id.value = decision;
                doUpdateLetterboxItem(data, decision);
                break;

            case ModalLetterboxModes.DELETE:
                confirmShow('Delete Letterbox Item', 'Are you sure you want to delete this letterbox item?', result =>
                    {
                        doDelete();
                    })
                break;

            default:
                data.letterbox.letterbox_decision_type_id.value = decision;
                doUpdateLetterboxItem(data, decision);
        }
    }

    const onClickSaveButton = (decision) => {
        handleSubmit(onSubmit)(decision);
    }

    const validateDropdownTo = ( val ) =>
    {
        if ( +val.value === 0 )
        {
            return "You must select the person the item is being sent to." ;
        }
    }

    const validateDropdownFrom = ( val ) =>
    {
        if ( +val.value === 0 )
        {
            return "You must select the person the item is being sent from." ;
        }
    }

    const validateDropdownType = ( val ) =>
    {
        if ( +val.value === 0 )
        {
            return "You must select an item type" ;
        }
    }

    const uidFromDropdown = ( selection ) =>
    {
        return selection.value !== undefined ? selection.value : null ;
    }

    const readonlyFieldStyle = {
        color: 'rgb(66, 139, 205)'
    }

    const handleUnsetDocument = () => {
        setValue('letterbox.document', null);
        const dataHolder = { ...data.data, document_array: [] };
        setData({ ...data, data: dataHolder });
    }

    //For Message document attachment
    const getDocumentUrl = (  ) =>
    {
        let url = '/getFile.php?enc_docid=';
        url = data.data.message !== undefined ? url + data.data.message.document_array[0] : url + data.data.document_id;
        return url;
    }

    const getDocumentIcon = (  ) =>
    {
        let icon = IconPath.ICON_DOCUMENT;
        return icon;
    }

    const isReadOnly = props.mode === ModalLetterboxModes.VIEW || props.mode === ModalLetterboxModes.DELETE || props.mode === ModalLetterboxModes.ACTION;
    const isReadOnlyOrEdit = isReadOnly || props.mode === ModalLetterboxModes.EDIT;
    const readOnlyMode = props.mode !== ModalLetterboxModes.CREATE ;
    const readOnlyModeOrEdit = ( props.mode === ModalLetterboxModes.CREATE || props.mode === ModalLetterboxModes.EDIT ) ? false : true ;

    const renderLetterboxItem = ( prop )  =>
    {
        const decisionReadOnlyMode = readOnlyMode || props.mode === ModalLetterboxModes.CREATE ;
        const isTypeMessage = +(data.data.letterbox_item_type_id) === LetterBoxType.MESSAGE ? true : false;

        return (
            <Fragment>
                <form className='registration-form' onSubmit={handleSubmit(onSubmit)}>
                    { isReadOnly && <div>
                        <label>Type</label>
                        <div className='form-control' style={readonlyFieldStyle}>{data.data.letterbox_item_type}</div>
                    </div>}

                    { !isReadOnly && <div>
                        <label>Type</label>
                        <DropdownLetterboxItemTypes readOnly={readOnlyMode} name='letterbox.letterbox_item_type_id' validate={validateDropdownType} control={control} options={{isRawValue: true}}/>
                        { formErrors.letterbox?.letterbox_item_type_id && <ErrorDiv>{formErrors.letterbox.letterbox_item_type_id.message}</ErrorDiv>}
                    </div>}


                    {!isTypeMessage && < div > < label > More Details</label>
                        <textarea readOnly={readOnlyModeOrEdit} style={{minHeight: '5em'}} className='form-control' {...register("letterbox.details")} />
                    </div>}

                    { isReadOnlyOrEdit && <div style={{display: 'grid', gridTemplateColumns: '50% 50%', columnGap: '1rem'}}>
                        <div>
                            <label>Addressed To</label>
                            <div className='form-control' style={readonlyFieldStyle}>{data.data.to_name}</div>
                        </div>

                        <div>
                            <label>From</label>
                            <div className='form-control' style={readonlyFieldStyle}>{data.data.from_name}</div>
                        </div>
                    </div>}

                    { !isReadOnlyOrEdit && <div>
                        <label>Addressed To</label>
                        <DropdownLetterboxToUsers readOnly={readOnlyMode} name='letterbox.to_uid' validate={validateDropdownTo} control={control} options={{isRawValue: true}} />
                        { formErrors.letterbox?.to_uid && <ErrorDiv>{formErrors.letterbox.to_uid.message}</ErrorDiv>}
                    </div>}

                    { !isReadOnlyOrEdit && <div>
                        <label>From</label>
                        <DropdownLetterboxToContacts readOnly={readOnlyMode} name='letterbox.from_uid' to_uid={uidFromDropdown(watchToId)} validate={validateDropdownFrom} control={control} options={{isRawValue: true}}/>
                        { formErrors.letterbox?.from_uid && <ErrorDiv>{formErrors.letterbox.from_uid.message}</ErrorDiv>}
                    </div>}

                    <div style={{display: 'grid', gridTemplateColumns: '50% 50%', columnGap: '1rem'}}>
                        {!isTypeMessage && <div>
                            <label>Reference Number</label>
                            <input readOnly={readOnlyMode} className='form-control' {...register("letterbox.reference_number")} />
                            { formErrors.reference_number && <ErrorDiv>Please enter a reference number</ErrorDiv> }
                        </div>}

                        <div>
                            <label>Status</label>
                            <DropdownLetterboxDecisions readOnly={decisionReadOnlyMode} name='letterbox.letterbox_decision_type_id' control={control}/>
                            { formErrors.letterbox_decision_type_id && <ErrorDiv>{formErrors.letterbox_decision_type_id.message}</ErrorDiv>}
                        </div>
                    </div>

                    { messageSection() }

                    { attachmentsSection() }

                    { commentSection() }

                    { buttonSection() }
                </form>

            </Fragment>
        )
    }

    const messageSection = () =>
    {
        if ( +(data.data.letterbox_item_type_id) === LetterBoxType.MESSAGE )
        {
            return (
                <Fragment>
                    <label>Message Title</label>
                    <input readOnly={true} className='form-control' {...register("message.title")} />

                    <label>Message Text</label>
                    <textarea readOnly={true} style={{minHeight: '5em'}} className='form-control'  {...register("message.text")} />
                </Fragment>
            )
        }
    }

    const attachmentsSection = () =>
    {
        //Attachments
        const hasImage = isReadOnly && data.data.message !== undefined && (data.data.message.image_array.length > 0);
        const hasMessageDocument = isReadOnly && data.data.message !== undefined && (data.data.message.document_array.length > 0 || data.data.attached_pdf_document_id != null);
        const hasLetterboxDocument = isReadOnlyOrEdit && data.data.document_id !== undefined && data.data.document_id != null;
        const hasDocument = hasMessageDocument || hasLetterboxDocument;
        const hasAttachment = hasImage || hasDocument;

        return (
            <Fragment>
                {isReadOnlyOrEdit && hasAttachment && <div><label>Attachment</label></div>}

                {(isReadOnly && hasImage) && <div className="profile-img-container-wrap profile-img-container-wrap_child">
                    { data.data.message.image_array.map((imageData)=>{
                        return (
                            <Fragment>
                                <div className="profile-img-wrap" style={{marginRight: '5px', marginBottom: '10px', marginTop: '10px'}}>
                                    {isReadOnly &&
                                        <img
                                            src={getChildImageUrl(imageData, data.data.user_id)}
                                            alt={'Child Image'}
                                        />
                                    }
                                </div>
                            </Fragment>
                        )
                    })}
                </div>}

                <div>
                    {props.mode !== ModalLetterboxModes.EDIT && props.mode !== ModalLetterboxModes.CREATE && hasDocument && <div>
                        <div style={{position: 'relative', marginRight: '5px', marginBottom: '10px', marginTop: '10px'}}>
                            <a href={getDocumentUrl()} target="_blank" ><img alt="document" width="70" src={getDocumentIcon()}/></a>
                        </div>
                    </div>}

                    {(props.mode === ModalLetterboxModes.EDIT && hasDocument && watchDocument !== null) &&
                        <div>
                            <div style={{position: 'relative', marginRight: '5px', marginBottom: '10px', marginTop: '10px'}}>
                                <a href={getDocumentUrl()} target="_blank" ><img alt="document" width="70" src={getDocumentIcon()}/></a>
                                {!isReadOnly && <div>
                                    <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-right">Remove Document</Tooltip>}>
                                        <span onClick={() => handleUnsetDocument()} style={{position: 'absolute', top: '-15px', fontSize: '2.3rem', color: 'red', lineHeight: '8px', cursor: 'pointer'}}>x</span>
                                    </OverlayTrigger>
                                </div>}
                            </div>
                        </div>
                    }

                    {!isReadOnly && <div>
                        <label>Add a Document</label>
                        <SingleFilePicker name='letterbox.document' control={control } options={{ maxSizeMb: 20, accept: ['.doc', '.pdf', '.docx']}} unsetDocument={handleUnsetDocument}/>
                    </div>}
                </div>
            </Fragment>
        )
    }

    const commentSection = () =>
    {
        const hasEditorComment = data.data.message !== undefined && data.data.message.approval_comment !== null && data.data.message.approval_comment !== '';

        return (
            <Fragment>
                {hasEditorComment && <div>
                    <label>Editor Comment</label>
                    <textarea readOnly={true} style={{minHeight: '5em'}} className='form-control'  {...register("message.editor_comment")} />
                </div>}

                <label>Letterbox Comment</label>
                <textarea readOnly={false} style={{minHeight: '5em'}} className='form-control' name='letterbox.letterbox_comment' {...register("letterbox.letterbox_comment")} />
            </Fragment>
        )
    }

    const userIsLetterboxApprover = () =>
    {
        return doesUserHaveApplication(ApplicationTypes.POSTBOX_APPROVER, globals.userType) ;
    }

    const userIsLetterboxAdmin = () =>
    {
        return doesUserHaveApplication(ApplicationTypes.POSTBOX, globals.userType) ;
    }

    const buttonSection = () =>
    {
        //Letterbox application permissions
        const existingUsersCanMessage = permissions.data.is_messager ? true : false;
        const shouldShowForward =  userIsLetterboxAdmin();
        const shouldShowToApprove = userIsLetterboxApprover();
        const canMessage = (newUsersCanMessage === true  || existingUsersCanMessage) ? true : false;
        const shouldShowForwardAsMessage = canMessage && userIsLetterboxApprover();
        const itemType = data.data.letterbox_item_type_id;
        const source = data.data.message !== undefined ?  data.data.message.source : null;
        const currentDecision = data.data.letterbox_decision_type_id;

        //If letterbox type is message and created automatically (not by letterbox staff)
        if ( +(data.data.letterbox_item_type_id) === LetterBoxType.MESSAGE && +source === MessageSource.SENDER)
        {
            return (
                <Fragment>
                    <div className="d-flex flex-row justify-content-end" style={{marginTop: '15px'}}>
                        <button onClick={onClickCancel} className="btn btn-secondary">Close<i className="et-arrow-bold-left" /></button>
                        {!isViewOnly && submitButton('return', () => onClickSaveButton(LetterBoxDecision.RETURN_TO_SENDER))}
                        {!isViewOnly && submitButton('approve', () => onClickSaveButton(LetterBoxDecision.APPROVED_AND_FORWARDED))}
                    </div>
                </Fragment>
            )
        }
        //Letterbox admin marking this item as 'sent on'
        else if (props.mode === ModalLetterboxModes.MARK_SENT)
        {
            return (
                <Fragment>
                    <div className="d-flex flex-row justify-content-end" style={{marginTop: '15px'}}>
                        <button onClick={onClickCancel} className="btn btn-secondary">Close<i
                            className="et-arrow-bold-left"/></button>
                        {!isViewOnly && submitButton('mark_sent', () => onClickSaveButton(LetterBoxDecision.APPROVED_AND_FORWARDED))}
                    </div>
                </Fragment>
            )
        }
        //Letterbox approver send on as digital message and has been rejectd by the child's editor
        else if (props.mode === ModalLetterboxModes.RESUBMIT)
        {
            return (
                <Fragment>
                    <div className="d-flex flex-row justify-content-end" style={{marginTop: '15px'}}>
                        <button onClick={onClickCancel} className="btn btn-secondary">Close<i
                            className="et-arrow-bold-left"/></button>
                        {!isViewOnly && submitButton('re_submit', () => onClickSaveButton(LetterBoxDecision.UNAPPROVED))}
                    </div>
                </Fragment>
            )
        }
        //All other use cases
        else{
            return (
                <Fragment>
                    <div className="d-flex flex-row justify-content-end" style={{marginTop: '15px'}}>
                        <button onClick={onClickCancel} className="btn btn-secondary">Close<i className="et-arrow-bold-left" /></button>
                        {!isViewOnly && shouldShowToApprove && !(itemType === LetterBoxType.MESSAGE && source === MessageSource.SENDER)
                            && +currentDecision !== LetterBoxDecision.NOT_APPROVED && submitButton('hold', () => onClickSaveButton(LetterBoxDecision.NOT_APPROVED))}
                        {!isViewOnly && shouldShowForward && !shouldShowToApprove && submitButton('create', () => onClickSaveButton(LetterBoxDecision.UNAPPROVED))}
                        {!isViewOnly && shouldShowToApprove && source === null && submitButton('approve', () => onClickSaveButton(LetterBoxDecision.APPROVED_NOT_FORWARDED))}
                        {!isViewOnly && shouldShowToApprove && source !== null && submitButton('approve', () => onClickSaveButton(LetterBoxDecision.APPROVED_AND_FORWARDED))}
                        {!isViewOnly && shouldShowForwardAsMessage && source === null && submitButton('send', () => onClickSaveButton(LetterBoxDecision.APPROVED_AND_FORWARDED))}
                    </div>
                </Fragment>
            )
        }
    }

    const renderApprovalForm = ( prop )  =>
    {
        const isTypeMessage = +(data.data.letterbox_item_type_id) === LetterBoxType.MESSAGE ? true : false;

        return (
            <Fragment>
                <form className='registration-form' onSubmit={handleSubmit(onSubmit)}>

                    <div>
                        <label>Type</label>
                        <div className='form-control' style={readonlyFieldStyle}>{data.data.letterbox_item_type}</div>
                    </div>

                    { !isTypeMessage && <div>
                        <label>More Details</label>
                        <textarea readOnly={true} style={{minHeight: '5em'}} className='form-control' {...register("letterbox.details")} />
                    </div> }

                    <div style={{display: 'grid', gridTemplateColumns: '50% 50%', columnGap: '1rem'}}>
                        <div>
                            <label>Item From</label>
                            <div className='form-control' style={readonlyFieldStyle}>{data.data.from_name}</div>
                        </div>
                        <div>
                            <label>Item Addressed To</label>
                            <div className='form-control' style={readonlyFieldStyle}>{data.data.to_name}</div>
                        </div>
                    </div>

                    <label>Status</label>
                    <DropdownLetterboxDecisions readOnly={true} name='letterbox.letterbox_decision_type_id' control={control}/>
                    { formErrors.letterbox_decision_type_id && <ErrorDiv>{formErrors.letterbox_decision_type_id.message}</ErrorDiv>}

                    { messageSection() }

                    { attachmentsSection() }

                    { commentSection() }

                    { buttonSection() }
                </form>
            </Fragment>
        )
    }

    const renderMarkSentForm = ( prop )  =>
    {
        const isTypeMessage = +(data.data.letterbox_item_type_id) === LetterBoxType.MESSAGE ? true : false;

        return (
            <Fragment>
                <form className='registration-form' onSubmit={handleSubmit(onSubmit)}>

                    <div>
                        <label>Type</label>
                        <div className='form-control' style={readonlyFieldStyle}>{data.data.letterbox_item_type}</div>
                    </div>
                    { !isTypeMessage && <div>
                        <label>More Details</label>
                        <textarea readOnly={true} style={{minHeight: '5em'}} className='form-control' {...register("letterbox.details")} />
                    </div> }

                    <div style={{display: 'grid', gridTemplateColumns: '50% 50%', columnGap: '1rem'}}>
                        <div>
                            <label>Item From</label>
                            <div className='form-control' style={readonlyFieldStyle}>{data.data.from_name}</div>
                        </div>
                        <div>
                            <label>Item Addressed To</label>
                            <div className='form-control' style={readonlyFieldStyle}>{data.data.to_name}</div>
                        </div>
                    </div>

                    <label>Status</label>
                    <DropdownLetterboxDecisions readOnly={true} name='letterbox.letterbox_decision_type_id' control={control}/>
                    { formErrors.letterbox_decision_type_id && <ErrorDiv>{formErrors.letterbox_decision_type_id.message}</ErrorDiv>}

                    { messageSection() }

                    { commentSection() }

                    { buttonSection() }
                </form>

            </Fragment>
        )
    }

    return (
        <ModalContentWrapper className="modal-wrapper">
            <Title mode={props.mode} />

            <div style={{width: props.width}}>
                { data.isLoading && <SpinnerDiv /> }
                { !data.isLoading && (props.mode === ModalLetterboxModes.ACTION) && renderApprovalForm ()}
                { !data.isLoading && (props.mode === ModalLetterboxModes.MARK_SENT || props.mode === ModalLetterboxModes.RESUBMIT) && renderMarkSentForm ()}
                { !data.isLoading && (props.mode !== ModalLetterboxModes.ACTION && props.mode !== ModalLetterboxModes.MARK_SENT &&  props.mode !== 're_submit') && renderLetterboxItem()}
            </div>
        </ModalContentWrapper>
    )
}

export default ModalAddLetterboxItem;

ModalAddLetterboxItem.defaultProps =
{
    width: '600px'
}

ModalAddLetterboxItem.propTypes = {
    letter_box_item_id: PropTypes.string,
    width: PropTypes.string,
    mode: PropTypes.string.isRequired,
    onSaveCompleted: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};
